/**
 * Pure helpers for the Collections screens: status colours, client-side upload
 * validation, and build-progress derivation. Kept free of React so they can be
 * unit-tested directly and reused by the Base solution pages.
 */

import type { IndexDetailOut, IndexedDocumentOut } from "@/shared/api/indexes";

/** Collection status → Mantine colour. */
export const COLLECTION_STATUS_COLOR: Record<string, string> = {
  empty: "gray",
  building: "blue",
  ready: "green",
  failed: "red",
};

/** Document status → Mantine colour. */
export const DOCUMENT_STATUS_COLOR: Record<string, string> = {
  pending: "gray",
  indexed: "green",
  skipped: "yellow",
  failed: "red",
};

export function collectionStatusColor(status: string): string {
  return COLLECTION_STATUS_COLOR[status] ?? "gray";
}

export function documentStatusColor(status: string): string {
  return DOCUMENT_STATUS_COLOR[status] ?? "gray";
}

/** Shown wherever Veeva documents enter or sit in a collection (ADR-0030). */
export const VEEVA_ACCESS_NOTE =
  "Access is controlled by this collection's sharing, not by Veeva document security.";

export const SOURCE_LABEL: Record<string, string> = {
  blob: "Azure Blob",
  upload: "Upload",
  veeva: "Veeva Vault",
  derived: "Translation (generated)",
};

// ---------------------------------------------------------------------------
// Upload validation — mirrors the backend allow-list and size cap so a bad file
// is refused before it is sent. The backend still re-checks everything.
// ---------------------------------------------------------------------------

export const UPLOAD_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".pptx", ".csv", ".txt"] as const;
export const UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

export function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot).toLowerCase() : "";
}

/** `null` when the file may be uploaded, otherwise the reason it may not. */
export function validateUpload(file: { name: string; size: number }): string | null {
  const ext = extensionOf(file.name);
  if (!(UPLOAD_EXTENSIONS as readonly string[]).includes(ext)) {
    return `Unsupported type${ext ? ` (${ext})` : ""}. Allowed: PDF, Word, Excel, PowerPoint, CSV, text.`;
  }
  if (file.size > UPLOAD_MAX_BYTES) return "Larger than 50 MB.";
  if (file.size === 0) return "The file is empty.";
  return null;
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Build progress
// ---------------------------------------------------------------------------

/**
 * The `build_progress` object the detail response carries (todo-demo-04):
 * `documents_done / documents_total`, the document being processed, and
 * whether a runner has claimed the build yet. Declared here as optional so the
 * page works both before and after the regenerated schema includes it; when it
 * is absent, progress is derived from the per-document statuses instead.
 */
export interface ServerBuildProgress {
  build_progress?: {
    documents_done: number;
    documents_total: number;
    current_document?: string | null;
    /** `false` = queued, waiting for a runner to pick the build up. */
    claimed?: boolean;
  } | null;
}

export interface BuildProgress {
  done: number;
  total: number;
  /** The file being processed now, when the server reports it. */
  currentFile: string | null;
  /** `true` while the build waits for a runner (server-reported only). */
  queued: boolean;
  indexed: number;
  skipped: number;
  failed: number;
  pending: number;
}

function serverProgress(
  detail: IndexDetailOut & ServerBuildProgress,
): Pick<BuildProgress, "done" | "total" | "currentFile" | "queued"> | null {
  const p = detail.build_progress;
  if (!p || typeof p.documents_total !== "number" || typeof p.documents_done !== "number") {
    return null;
  }
  return {
    done: p.documents_done,
    total: p.documents_total,
    currentFile: p.current_document ?? null,
    queued: p.claimed === false,
  };
}

export function countStatuses(documents: readonly IndexedDocumentOut[]) {
  const counts = { indexed: 0, skipped: 0, failed: 0, pending: 0 };
  for (const d of documents) {
    if (d.status in counts) counts[d.status as keyof typeof counts] += 1;
  }
  return counts;
}

export function deriveBuildProgress(detail: IndexDetailOut): BuildProgress {
  const counts = countStatuses(detail.documents);
  const server = serverProgress(detail);
  if (server) return { ...server, ...counts };
  return {
    done: counts.indexed + counts.skipped + counts.failed,
    total: detail.documents.length,
    currentFile: null,
    queued: false,
    ...counts,
  };
}

// ---------------------------------------------------------------------------
// Veeva document listing
// ---------------------------------------------------------------------------

const FIELDS =
  "id, name__v, type__v, status__v, major_version_number__v, minor_version_number__v, filename__v, format__v, size__v";

/** VQL for the document listing. A search term becomes a Vault full-text FIND. */
export function veevaDocumentsVql(term: string): string {
  const trimmed = term.trim();
  if (!trimmed) return `SELECT ${FIELDS} FROM documents`;
  const escaped = trimmed.replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  return `SELECT ${FIELDS} FROM documents FIND ('${escaped}')`;
}

export function versionLabel(d: {
  major_version: number | null;
  minor_version: number | null;
}): string {
  return d.major_version === null ? "current" : `v${d.major_version}.${d.minor_version ?? 0}`;
}

/** "Created by …" — resolved server-side at view time, never stored. */
export function ownerLabel(c: {
  owner_display_name: string | null;
  owner_email: string | null;
}): string {
  return c.owner_display_name || c.owner_email || "-";
}

// ---------------------------------------------------------------------------
// Translations (Create → Translate) nested under their original
// ---------------------------------------------------------------------------

export interface NestedDocument {
  doc: IndexedDocumentOut;
  /** 0 = an original (or a translation whose original was removed); 1 = a
   *  translation shown under its original. */
  depth: 0 | 1;
}

/** Documents in display order: each original followed by its translations
 *  (by language). A translation whose original is gone stays at the top level. */
export function nestTranslations(docs: IndexedDocumentOut[]): NestedDocument[] {
  const ids = new Set(docs.map((d) => d.document_id));
  const children = new Map<string, IndexedDocumentOut[]>();
  for (const d of docs) {
    const parent = d.derived_from_document_id;
    if (parent && ids.has(parent)) {
      children.set(parent, [...(children.get(parent) ?? []), d]);
    }
  }
  const out: NestedDocument[] = [];
  for (const d of docs) {
    const parent = d.derived_from_document_id;
    if (parent && ids.has(parent)) continue;
    out.push({ doc: d, depth: 0 });
    const kids = [...(children.get(d.document_id) ?? [])].sort((a, b) =>
      (a.language ?? "").localeCompare(b.language ?? ""),
    );
    for (const k of kids) out.push({ doc: k, depth: 1 });
  }
  return out;
}

/** "Translations: DE, FR" for an original, or `null` when it has none. */
export function translationsLabel(docs: IndexedDocumentOut[], originalId: string): string | null {
  const langs = docs
    .filter((d) => d.derived_from_document_id === originalId && d.language)
    .map((d) => (d.language ?? "").toUpperCase())
    .sort();
  return langs.length ? `Translations: ${langs.join(", ")}` : null;
}
